import React, { useCallback, useMemo, useState } from 'react';

import UIcon from 'Components/unit/UIcon/UIcon';
import { COLORS } from 'Components/foundation';

import styles from './SNewContentCard.style';

export const MODES = Object.freeze({
  STANDARD: 'standard',
  FIRST_CAMPAIGN_SCHEDULING: 'first_campaign_scheduling',
  CAMPAIGN_SCHEDULING: 'campaign_scheduling',
  FIRST: 'first',
  ACCENTUATED: 'accentuated',
  DISABLED: 'disabled',
} as const);

type ContentMode = typeof MODES[keyof typeof MODES];

export type SNewContentCardProps = {
  onClick: React.MouseEventHandler<HTMLDivElement>;
  type?: ContentMode;
  label?: string;
  style?: React.CSSProperties;
};

const STYLE: {
  [key in ContentMode]: {
    DEFAULT: React.CSSProperties;
    HOVER: React.CSSProperties;
  };
} = {
  [MODES.STANDARD]: {
    DEFAULT: {
      color: COLORS.TEXT.SECONDARY_DEFAULT,
      backgroundColor: COLORS.GREY_MEDIUM.DEFAULT,
    },
    HOVER: {
      color: COLORS.TEXT.SECONDARY_DEFAULT,
      backgroundColor: COLORS.GREY_LIGHT.HOVER,
    },
  },
  [MODES.FIRST_CAMPAIGN_SCHEDULING]: {
    DEFAULT: {
      color: COLORS.PRIMARY.DEFAULT,
      backgroundColor: `${COLORS.PRIMARY_LIGHT.DEFAULT}C8`, // Opacity of 200 (C8 in hex)
    },
    HOVER: {
      color: COLORS.PRIMARY.DEFAULT,
      backgroundColor: `${COLORS.PRIMARY_LIGHT.DEFAULT}64`, // Opacity of 100 (64 in hex)
    },
  },
  [MODES.CAMPAIGN_SCHEDULING]: {
    DEFAULT: {
      color: COLORS.TEXT.SECONDARY_DEFAULT,
      backgroundColor: COLORS.GREY_MEDIUM.DEFAULT,
    },
    HOVER: {
      color: COLORS.TEXT.SECONDARY_DEFAULT,
      backgroundColor: COLORS.GREY_LIGHT.HOVER,
    },
  },
  [MODES.FIRST]: {
    DEFAULT: {
      color: COLORS.PRIMARY.DEFAULT,
      backgroundColor: `${COLORS.PRIMARY_LIGHT.DEFAULT}C8`,
    },
    HOVER: {
      color: COLORS.PRIMARY.DEFAULT,
      backgroundColor: `${COLORS.PRIMARY_LIGHT.DEFAULT}64`,
    },
  },
  [MODES.ACCENTUATED]: {
    DEFAULT: {
      color: COLORS.WHITE.DEFAULT,
      backgroundColor: COLORS.PRIMARY.DEFAULT,
    },
    HOVER: {
      color: COLORS.WHITE.DEFAULT,
      backgroundColor: COLORS.PRIMARY.HOVER,
    },
  },
  [MODES.DISABLED]: {
    DEFAULT: {
      color: COLORS.GREY_DARK.DEFAULT,
      backgroundColor: COLORS.GREY_DARK.DISABLED,
      cursor: 'default',
    },
    HOVER: {
      color: COLORS.GREY_DARK.DEFAULT,
      backgroundColor: COLORS.GREY_DARK.DISABLED,
      cursor: 'default',
    },
  },
};

export const SNewContentCard: React.FC<SNewContentCardProps> = ({
  type = MODES.STANDARD,
  label = '',
  style,
  onClick,
}) => {
  const [isHover, setIsHover] = useState(false);
  const hoverState = useMemo(() => (isHover ? 'HOVER' : 'DEFAULT'), [isHover]);

  const handleHover = useCallback(() => setIsHover(true), []);
  const handleLeave = useCallback(() => setIsHover(false), []);
  const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (type === MODES.DISABLED) return;
      onClick(event);
    },
    [onClick, type],
  );

  return (
    <div
      id="new-content-card"
      className="nonDraggable"
      data-tracking-action="create"
      data-tracking-context="content"
      onMouseEnter={handleHover}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      style={{
        ...styles.cardWrapper,
        ...STYLE[type][hoverState],
        ...style,
      }}
    >
      <UIcon name="plus-light" size={17} color={STYLE[type][hoverState].color} />
      {type === MODES.STANDARD || type === MODES.DISABLED ? null : (
        <h2 style={{ ...styles.label, color: STYLE[type].DEFAULT.color }}>{label}</h2>
      )}
    </div>
  );
};

export default SNewContentCard;
